import React, { useRef, useState } from "react"
import axios from "axios"
import Cropper from "react-cropper"
import "cropperjs/dist/cropper.css"
import { Upload, X, Check, Crop, Loader2, Trash2, AlertTriangle } from "lucide-react"
import { Button, Badge, message } from "../../ui/ot"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../../ui/card"
// import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../ui/accordion"
// import { Badge } from "@/components/ui/badge"
import { Progress } from "../../ui/progress"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog"
// import { Input } from "@/components/ui/input"
// import { uploadBookImages, saveProcessingHistory } from "@/lib/actions"
// import { useToast } from "@/hooks/use-toast"
// import type { ImageUploadItem, BatchProcessingRecord } from "@/lib/types"

const MAX_FILES_PER_BATCH = 1000
const MAX_CONCURRENT_UPLOADS = 2
const UPLOAD_TIMEOUT_MS = 30000
const ALLOWED_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"])
const ALLOWED_IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png"])

const createUploadId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID()
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error || new Error("圖片預覽讀取失敗"))
    reader.readAsDataURL(file)
})

const getFileExtension = (fileName = "") => fileName.split(".").pop()?.toLowerCase() || ""

const validateImageFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.has(file.type) || !ALLOWED_IMAGE_EXTENSIONS.has(getFileExtension(file.name))) {
        return `${file.name}：僅支援 JPG/JPEG、PNG 格式`
    }
    return null
}

const canvasToBlob = (canvas, type) => new Promise((resolve, reject) => {
    canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("圖片裁切失敗")),
        type,
        type === "image/png" ? undefined : 0.92,
    )
})

const uploadFile = async ({ file, id }) => {
    const formData = new FormData()
    formData.append("inputFile", file)
    formData.append("upload_request_id", id)

    const response = await axios.post("/koha/cover_images/file", formData, {
        timeout: UPLOAD_TIMEOUT_MS,
    })
    return response.data
}

const BookImageUpload = () => {
    // const { toast } = useToast()
    const [uploadItems, setUploadItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [preparingFiles, setPreparingFiles] = useState(false)
    const [progress, setProgress] = useState(0)
    const [error, setError] = useState(null)
    const [processingComplete, setProcessingComplete] = useState(true)
    const [cropItemId, setCropItemId] = useState(null)
    const [cropping, setCropping] = useState(false)
    const cropperRef = useRef(null)

    const prepareSelectedFiles = async (selectedFiles) => {
        if (!selectedFiles || selectedFiles.length === 0) return

        const validationErrors = selectedFiles.map(validateImageFile).filter(Boolean)
        const validFiles = selectedFiles.filter((file) => !validateImageFile(file))
        if (validationErrors.length > 0) {
            const validationMessage = validationErrors.join("\n")
            setError(validationMessage)
            message.error(validationMessage)
        }
        if (validFiles.length === 0) return

        if (uploadItems.length + validFiles.length > MAX_FILES_PER_BATCH) {
            message.error(`一次最多只能選擇 ${MAX_FILES_PER_BATCH} 張圖片`)
            return
        }

        // Reset states
        if (validationErrors.length === 0) setError(null)
        setProcessingComplete(false)

        setPreparingFiles(true)
        try {
            const newItems = await Promise.all(validFiles.map(async (file) => {
                const preview = await readFileAsDataUrl(file)
                return {
                    id: createUploadId(),
                    file,
                    preview,
                    status: "pending",
                    bookInfo: null,
                    error: null,
                }
            }))
            setUploadItems((prev) => [...prev, ...newItems])
        } catch (readError) {
            setError("圖片預覽讀取失敗，請重新選擇檔案")
            message.error("圖片預覽讀取失敗，請重新選擇檔案")
        } finally {
            setPreparingFiles(false)
        }
    }

    const handleFileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files || [])
        e.target.value = ""
        await prepareSelectedFiles(selectedFiles)
    }

    const handleDrop = async (e) => {
        e.preventDefault()
        if (loading || preparingFiles) return
        await prepareSelectedFiles(Array.from(e.dataTransfer.files || []))
    }

    const handleApplyCrop = async () => {
        const cropper = cropperRef.current?.cropper
        const cropItem = uploadItems.find((item) => item.id === cropItemId)
        if (!cropper || !cropItem) return

        setCropping(true)
        try {
            const canvas = cropper.getCroppedCanvas({
                maxWidth: 2400,
                maxHeight: 3200,
                imageSmoothingEnabled: true,
                imageSmoothingQuality: "high",
            })
            if (!canvas) throw new Error("圖片裁切失敗")

            const outputType = cropItem.file.type === "image/png" ? "image/png" : "image/jpeg"
            const blob = await canvasToBlob(canvas, outputType)
            const croppedFile = new File([blob], cropItem.file.name, {
                type: outputType,
                lastModified: Date.now(),
            })
            const validationError = validateImageFile(croppedFile)
            if (validationError) throw new Error(validationError)

            const preview = await readFileAsDataUrl(croppedFile)
            setUploadItems((prev) => prev.map((item) => item.id === cropItemId
                ? {
                    ...item,
                    file: croppedFile,
                    preview,
                    status: "pending",
                    error: null,
                    cropped: true,
                }
                : item))
            setProcessingComplete(false)
            setError(null)
            setCropItemId(null)
            message.success("圖片裁切完成")
        } catch (cropError) {
            message.error(cropError?.message || "圖片裁切失敗，請重試")
        } finally {
            setCropping(false)
        }
    }
    const handleUpload = async () => {
        const pendingItems = uploadItems.filter((item) => item.status === "pending")
        if (pendingItems.length === 0) return

        setLoading(true)
        setError(null)
        setProgress(0)
        setProcessingComplete(false)
        const totalItems = pendingItems.length
        let completed = 0
        const queue = [...pendingItems]

        const uploadWorker = async () => {
            while (queue.length > 0) {
                const { file, id } = queue.shift()
                try {
                    const responseData = await uploadFile({ file, id })
                    const d = responseData?.values ?? {}

                    setUploadItems(prev => {
                        return prev.map((item) => {
                            if (item.id !== id) return item
                            if (responseData?.status === "success" && d?.file_id && d?.biblionumber) {
                                return { ...item, ...d, status: "success" }
                            }
                            if (responseData?.status === "success" && d?.file_id && !d?.biblionumber) {
                                return { ...item, ...d, status: "warning", error: d?.error }
                            }
                            return { ...item, ...d, status: "error", error: d?.error || "處理失敗，請重試" }
                        })
                    })
                } catch (uploadError) {
                    const errorMessage = uploadError?.response?.data?.values?.error
                        || (uploadError?.code === "ECONNABORTED"
                            ? "上傳逾時，請確認處理結果後再重試"
                            : "上傳失敗，請確認處理結果後再重試")
                    setUploadItems(prev => {
                        return prev.map((item) => item.id === id
                            ? { ...item, status: "error", error: errorMessage }
                            : item)
                    })
                } finally {
                    completed += 1
                    setProgress(Math.floor((completed / totalItems) * 100))
                }
            }
        }

        await Promise.all(Array.from(
            { length: Math.min(MAX_CONCURRENT_UPLOADS, totalItems) },
            () => uploadWorker(),
        ))

        setLoading(false)
        setProcessingComplete(true)
    }


    const handleRemoveItem = (id) => {
        setUploadItems((prev) => prev.filter((item) => item.id !== id))
    }

    const resetForm = () => {
        setUploadItems([])
        setError(null)
        setProgress(0)
        setProcessingComplete(false)
    }
    const successCount = uploadItems.filter((item) => item?.status === "success").length
    const warningCount = uploadItems.filter((item) => item?.status === "warning").length
    const errorCount = uploadItems.filter((item) => item?.status === "error").length
    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                        <span>批次上傳書目圖片</span>
                        {uploadItems.length > 0 && (
                            <div className="flex gap-2">
                                <Badge variant="outline">{uploadItems.length} 個檔案</Badge>
                                {processingComplete && (
                                    <>
                                        {successCount > 0 && (
                                            <Badge variant="success" className="bg-green-100 text-green-800">
                                                {successCount} 成功
                                            </Badge>
                                        )}
                                        {warningCount > 0 && (
                                            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                                                {warningCount} 待綁定
                                            </Badge>
                                        )}
                                        {errorCount > 0 && <Badge variant="destructive">{errorCount} 失敗</Badge>}
                                    </>
                                )}
                            </div>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {error && (
                        <div className="mb-4 whitespace-pre-line rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}
                    {uploadItems.length > 0 && !processingComplete && (
                        <Button className="mb-2" onClick={handleUpload} disabled={loading || preparingFiles || uploadItems.length === 0}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {preparingFiles ? "準備圖片中..." : loading ? "處理中..." : "批次處理圖片"}
                        </Button>
                    )}
                    {uploadItems.length === 0 ? (
                        <div
                            className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed p-10"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                        >
                            <Upload className="h-10 w-10 text-muted-foreground" />
                            <div className="text-center">
                                <p className="text-sm font-medium">拖放或點擊上傳多個圖片</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                    支援 JPG/JPEG、PNG；每批最多 {MAX_FILES_PER_BATCH} 張
                                </p>
                            </div>
                            <label htmlFor="file-upload">
                                <div className="cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                                    選擇檔案
                                </div>
                                <input
                                    id="file-upload"
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg"
                                    className="sr-only"
                                    onChange={handleFileChange}
                                    multiple
                                    disabled={preparingFiles}
                                />
                            </label>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {loading && (
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>處理進度</span>
                                        <span>{progress}%</span>
                                    </div>
                                    <Progress value={progress} className="h-2" />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-8">
                                {uploadItems?.map((item, index) => (
                                    <div
                                        key={item.id}
                                        className={`relative aspect-[3/4] overflow-hidden rounded-md border ${item?.status === "success"
                                            ? "border-green-500"
                                            : item?.status === "warning"
                                                ? "border-yellow-500"
                                                : item?.status === "error"
                                                    ? "border-red-500"
                                                    : "border-gray-200"
                                            }`}
                                    >
                                        <img
                                            src={item?.preview || "/placeholder.svg"}
                                            alt={`書籍預覽 ${index + 1}`}
                                            className="h-full w-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <Button
                                                variant="secondary"
                                                size="icon"
                                                className="h-8 w-8"
                                                title="預覽與裁切"
                                                onClick={() => setCropItemId(item.id)}
                                                disabled={loading || preparingFiles || item?.status === "success" || item?.status === "warning"}
                                            >
                                                <Crop className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleRemoveItem(item.id)}
                                                disabled={loading || preparingFiles}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        {item?.status === "success" && (
                                            <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                                                <Check className="h-4 w-4" />
                                            </div>
                                        )}
                                        {item?.status === "warning" && (
                                            <div className="absolute top-2 right-2 bg-yellow-500 text-white rounded-full p-1">
                                                <AlertTriangle className="h-4 w-4" />
                                            </div>
                                        )}
                                        {item?.status === "error" && (
                                            <div className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1">
                                                <X className="h-4 w-4" />
                                            </div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-white text-xs truncate">
                                            {item?.cropped && <span className="mr-1">已裁切 ·</span>}
                                            {item?.file?.name || item?.file_client_name}
                                        </div>
                                    </div>
                                ))}

                                <div className="flex aspect-[3/4] items-center justify-center rounded-md border border-dashed">
                                    <label
                                        htmlFor="add-more-files"
                                        className="flex flex-col items-center justify-center cursor-pointer p-4"
                                    >
                                        <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                                        <span className="text-sm text-muted-foreground text-center">添加更多檔案</span>
                                        <input
                                            id="add-more-files"
                                            type="file"
                                            accept="image/png, image/jpeg, image/jpg"
                                            className="sr-only"
                                            onChange={handleFileChange}
                                            multiple
                                            disabled={loading || preparingFiles || uploadItems.length >= MAX_FILES_PER_BATCH}
                                        />
                                    </label>
                                </div>
                            </div>


                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-between">
                    <Button variant="outline" onClick={resetForm} disabled={loading || preparingFiles || uploadItems.length === 0}>
                        清除全部
                    </Button>
                    
                </CardFooter>
            </Card>

            {processingComplete && (
                <Card>
                    <CardHeader>
                        <CardTitle>處理結果</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Accordion type="multiple" className="space-y-2">
                            {uploadItems.map((item) => (
                                <AccordionItem
                                    key={item.id}
                                    value={`item-${item.id}`}
                                    className={`border rounded-md ${item?.status === "success"
                                        ? "border-green-200 bg-green-50"
                                        : item?.status === "warning"
                                            ? "border-yellow-200 bg-yellow-50"
                                            : item?.status === "error"
                                                ? "border-red-200 bg-red-50"
                                                : "border-gray-200"
                                        }`}
                                >
                                    <AccordionTrigger className="px-4 py-2 hover:no-underline">
                                        <div className="flex items-center gap-2 text-sm">
                                            <span className="font-medium truncate max-w-[200px]">{item?.file_client_name}</span>
                                            {item?.status === "success" && (
                                                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                                                    成功
                                                </Badge>
                                            )}
                                            {item?.status === "warning" && (
                                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                                                    待綁定
                                                </Badge>
                                            )}
                                            {item?.status === "error" && (
                                                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200">
                                                    失敗
                                                </Badge>
                                            )}
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 pb-3 pt-1">
                                        {item?.status === "success" ? (
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="font-medium">書目編號</div>
                                                    <div className="col-span-2">{item?.biblionumber}</div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="font-medium">書名</div>
                                                    <div className="col-span-2">{item?.title}</div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="font-medium">作者</div>
                                                    <div className="col-span-2">{item?.author}</div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="font-medium">出版商</div>
                                                    <div className="col-span-2">{item?.publish_publisher}{item?.publish_year}{item?.publish_placepublish_place}</div>
                                                </div>
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div className="font-medium">ISBN</div>
                                                    <div className="col-span-2">{item?.isbn}</div>
                                                </div>
                                                {/* <div className="flex justify-end mt-4">
                                                    <Button size="sm" onClick={() => handleConfirmItem(index)}>
                                                        <Check className="mr-2 h-3 w-3" />
                                                        確認綁定
                                                    </Button>
                                                </div> */}
                                            </div>
                                        ) : item?.status === "warning" ? (
                                            <div className="space-y-3">
                                                <div className="text-yellow-600 mb-3">{item?.error}</div>
                                                {/* <div className="flex justify-end">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="bg-yellow-100 border-yellow-300 text-yellow-800"
                                                    >
                                                        <AlertTriangle className="mr-2 h-3 w-3" />
                                                        手動綁定書目
                                                    </Button>
                                                </div> */}
                                            </div>
                                        ) : item?.status === "error" ? (
                                            <div className="text-red-600">{item?.error || "處理失敗，請重試"}</div>
                                        ) : (
                                            <div className="text-muted-foreground">等待處理...</div>
                                        )}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </CardContent>
                </Card>
            )}

            <Dialog open={Boolean(cropItemId)} onOpenChange={(open) => !open && !cropping && setCropItemId(null)}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
                    <DialogHeader>
                        <DialogTitle>圖片預覽與裁切</DialogTitle>
                        <DialogDescription>
                            拖曳圖片或裁切框調整範圍，滾動滑鼠滾輪可縮放。套用後仍需點擊「批次處理圖片」才會上傳。
                        </DialogDescription>
                    </DialogHeader>
                    {cropItemId && (() => {
                        const cropItem = uploadItems.find((item) => item.id === cropItemId)
                        if (!cropItem) return null
                        return (
                            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_240px]">
                                <div className="min-h-[420px] overflow-hidden rounded-md bg-black">
                                    <Cropper
                                        key={`${cropItem.id}-${cropItem.preview}`}
                                        ref={cropperRef}
                                        src={cropItem.preview}
                                        style={{ height: 520, width: "100%" }}
                                        viewMode={1}
                                        dragMode="move"
                                        initialAspectRatio={3 / 4}
                                        autoCropArea={0.9}
                                        background={false}
                                        responsive
                                        restore={false}
                                        guides
                                        preview=".book-cover-crop-preview"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <p className="text-sm font-medium">裁切預覽</p>
                                    <div className="book-cover-crop-preview aspect-[3/4] w-full overflow-hidden rounded-md border bg-muted" />
                                    <div className="space-y-1 text-xs text-muted-foreground">
                                        <p className="break-all">{cropItem.file.name}</p>
                                        <p>{(cropItem.file.size / 1024 / 1024).toFixed(2)} MB</p>
                                        <p>輸出最長邊不超過 3200 px</p>
                                    </div>
                                </div>
                            </div>
                        )
                    })()}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCropItemId(null)} disabled={cropping}>
                            取消
                        </Button>
                        <Button onClick={handleApplyCrop} disabled={cropping}>
                            {cropping && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {cropping ? "處理中..." : "套用裁切"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
export default BookImageUpload
